/**
 * Bucket Assignment Modal
 * Modal for assigning accounts to strategic buckets
 */
import { useState, useEffect, useMemo } from 'react';
import { useAssignAccountToBucket } from '@/hooks/useAllocation';
import { useAccounts } from '@/hooks/useAccounts';
import Button from '@/components/ui/Button';
import {
  STRATEGIC_ROLE_DISPLAY_NAMES,
  type StrategicRole,
  type AssignAccountToBucketRequest,
} from '@/types/allocation';

interface BucketAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  accountId?: string; // Optional - pre-select if coming from account context
}

interface BucketAssignment {
  role: StrategicRole | '';
  percentage: number;
}

const STRATEGIC_ROLES: StrategicRole[] = [
  'emergency_fund',
  'short_term_savings',
  'retirement_pretax',
  'retirement_roth',
  'retirement_taxable',
  'education',
  'healthcare',
  'general_investment',
];

export default function BucketAssignmentModal({
  isOpen,
  onClose,
  accountId,
}: BucketAssignmentModalProps) {
  const { data: accountsData } = useAccounts();
  const { mutate: assignToBucket, isPending } = useAssignAccountToBucket();

  // Form state
  const [selectedAccountId, setSelectedAccountId] = useState<string>(accountId ?? '');
  const [isSplit, setIsSplit] = useState(false);
  const [assignments, setAssignments] = useState<BucketAssignment[]>([
    { role: '', percentage: 100 },
  ]);

  // Get accounts list
  const accounts = useMemo(() => accountsData ?? [], [accountsData]);

  // Get selected account details
  const selectedAccount = useMemo(
    () => accounts.find((a) => a.id === selectedAccountId),
    [accounts, selectedAccountId]
  );

  // Reset form when accountId prop changes
  useEffect(() => {
    if (accountId) {
      setSelectedAccountId(accountId);
    }
  }, [accountId]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedAccountId(accountId ?? '');
      setIsSplit(false);
      setAssignments([{ role: '', percentage: 100 }]);
    }
  }, [isOpen, accountId]);

  if (!isOpen) return null;

  const handleAddSplit = () => {
    if (assignments.length < 3) {
      setAssignments([...assignments, { role: '', percentage: 0 }]);
    }
  };

  const handleRemoveSplit = (index: number) => {
    if (assignments.length > 1) {
      const newAssignments = assignments.filter((_, i) => i !== index);
      // Redistribute percentage to first assignment
      const totalRemaining = newAssignments.reduce((sum, a) => sum + a.percentage, 0);
      const firstAssignment = newAssignments[0];
      if (totalRemaining < 100 && firstAssignment) {
        firstAssignment.percentage += 100 - totalRemaining;
      }
      setAssignments(newAssignments);
    }
  };

  const handleRoleChange = (index: number, role: StrategicRole | '') => {
    const newAssignments = [...assignments];
    const assignment = newAssignments[index];
    if (assignment) {
      assignment.role = role;
      setAssignments(newAssignments);
    }
  };

  const handlePercentageChange = (index: number, percentage: number) => {
    const newAssignments = [...assignments];
    const assignment = newAssignments[index];
    if (assignment) {
      assignment.percentage = Math.max(0, Math.min(100, percentage));
      setAssignments(newAssignments);
    }
  };

  const handleSplitToggle = (split: boolean) => {
    setIsSplit(split);
    if (!split) {
      // Reset to single assignment
      setAssignments([{ role: assignments[0]?.role ?? '', percentage: 100 }]);
    }
  };

  const totalPercentage = assignments.reduce((sum, a) => sum + a.percentage, 0);
  const hasValidRoles = assignments.every((a) => a.role !== '');
  const canSubmit =
    selectedAccountId &&
    hasValidRoles &&
    totalPercentage === 100 &&
    assignments.length > 0;

  const handleSubmit = () => {
    if (!canSubmit) return;

    const assignmentsRecord: Record<StrategicRole, number> = {} as Record<StrategicRole, number>;
    for (const assignment of assignments) {
      if (assignment.role) {
        assignmentsRecord[assignment.role as StrategicRole] = assignment.percentage;
      }
    }

    const data: AssignAccountToBucketRequest = {
      accountId: selectedAccountId,
      assignments: assignmentsRecord,
    };

    assignToBucket(data, {
      onSuccess: () => {
        handleClose();
      },
    });
  };

  const handleClose = () => {
    setSelectedAccountId(accountId ?? '');
    setIsSplit(false);
    setAssignments([{ role: '', percentage: 100 }]);
    onClose();
  };

  // Get roles already used in assignments (to prevent duplicates)
  const usedRoles = new Set(assignments.map((a) => a.role).filter(Boolean));

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-lg transform bg-white rounded-lg shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Assign Account to Bucket
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <CloseIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 space-y-4">
            {/* Account selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account
              </label>
              <select
                value={selectedAccountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                disabled={!!accountId}
              >
                <option value="">Select an account...</option>
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name} ({account.type})
                  </option>
                ))}
              </select>
              {selectedAccount && (
                <p className="mt-1 text-sm text-gray-500">
                  Current balance: ${formatNumber(selectedAccount.balance)}
                </p>
              )}
            </div>

            {/* Split toggle */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium text-gray-700 mb-2">
                Bucket Assignment
              </p>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="splitMode"
                    checked={!isSplit}
                    onChange={() => handleSplitToggle(false)}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-600">Single bucket (100%)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="splitMode"
                    checked={isSplit}
                    onChange={() => handleSplitToggle(true)}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-600">Split between buckets</span>
                </label>
              </div>
            </div>

            {/* Bucket assignments */}
            <div className="space-y-3">
              {assignments.map((assignment, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Bucket {assignments.length > 1 ? index + 1 : ''}
                    </label>
                    <select
                      value={assignment.role}
                      onChange={(e) =>
                        handleRoleChange(index, e.target.value as StrategicRole | '')
                      }
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="">Select a bucket...</option>
                      {STRATEGIC_ROLES.map((role) => (
                        <option
                          key={role}
                          value={role}
                          disabled={usedRoles.has(role) && assignment.role !== role}
                        >
                          {STRATEGIC_ROLE_DISPLAY_NAMES[role]}
                        </option>
                      ))}
                    </select>
                  </div>
                  {isSplit && (
                    <>
                      <div className="w-24">
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          %
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            value={assignment.percentage}
                            onChange={(e) =>
                              handlePercentageChange(index, parseInt(e.target.value) || 0)
                            }
                            min="0"
                            max="100"
                            className="w-full pr-6 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                            %
                          </span>
                        </div>
                      </div>
                      {assignments.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveSplit(index)}
                          className="mt-5 p-1 text-gray-400 hover:text-red-500"
                          title="Remove"
                        >
                          <CloseIcon className="h-4 w-4" />
                        </button>
                      )}
                    </>
                  )}
                </div>
              ))}

              {isSplit && assignments.length < 3 && (
                <button
                  type="button"
                  onClick={handleAddSplit}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  + Add Split
                </button>
              )}

              {isSplit && totalPercentage !== 100 && (
                <p className="text-sm text-red-600">
                  Total must equal 100% (currently {totalPercentage}%)
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!canSubmit}
              isLoading={isPending}
            >
              Save Assignment
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

function formatNumber(value: string | number | null | undefined): string {
  if (value == null || value === '') return '0';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(num)) return '0';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(num);
}
